import moment from 'moment';
import { isEmpty } from '@intelligence/v3/utils/common';
import { pageParamsFormatMap } from '@intelligence/components/Filter/util';
import { toDateTypeFilter } from '@intelligence/utils/common';
import { GRANULARITY_MOMENT_HELPER_MAPPING } from '@intelligence/utils/dateUtils';
import { SortType } from '@home/enums/common';
import * as filterKeyMap from '@intelligence/components/Filter/FilterBase/_config';

// const GRANULARITIES: IGranularity[] = ['daily', 'weekly', 'monthly'];
/**
 * 校验日期粒度是否合法
 * @param granularities 可选的日期粒度列表
 * @param granularity 日期粒度
 * @return 日期粒度是否合法
 */
export const validateGranularity = (granularities?: IGranularity[], granularity?: unknown): boolean => {
  if (isEmpty(granularities)) {
    return false;
  }

  // @ts-ignore
  return granularities.includes(granularity as IGranularity);
};

/**
 * 格式化日期字符串
 * @param date 日期字符串
 * @param formatter 日期格式
 * @return 日期字符串（按指定日期格式 格式化）
 */
export const formatDateStr = (date?: string, formatter = 'YYYY-MM-DD') => {
  if (!date) return '';
  const dateM = moment(date);
  return !dateM.isValid() ? date : dateM.format(formatter);
};

/**
 * 格式化日期字符串或moment对象
 * @param date 日期字符串或moment对象
 * @param formatter 日期格式
 * @return 日期字符串（按指定日期格式 格式化）
 */
export const formatDateStrPlus = (date?: string | moment.Moment, formatter = 'YYYY-MM-DD'): string => {
  if (moment.isMoment(date)) {
    return !date.isValid() ? '' : date.format(formatter);
  }

  return formatDateStr(date, formatter);
};

/**
 * 转为moment后的日期，无法转为moment时或者转换后的moment是非法时，返回null
 * @param date 日期
 * @param format 日期字符串格式
 * @returns 转为moment后的日期，无法转为moment时或者转换后的moment是非法时，返回null
 */
export const getNullableMoment = (date?: string, format = 'YYYY-MM-DD'): INullableMoment => {
  if (!date) return null;
  const dateM = moment(date, format);
  return dateM?.isValid() ? dateM : null;
};

export const getDateComparator = (sortType: SortType) => {
  const isNullSort = sortType === SortType.None;
  const isAscSort = isNullSort ? false : sortType === SortType.Asc;

  return (aDate?: string, bDate?: string) => {
    if (isNullSort) return 0;
    const aDateM = getNullableMoment(aDate);
    const bDateM = getNullableMoment(bDate);
    if (!aDateM && !bDateM) return 0;
    if (!aDateM) return 1;
    if (!bDateM) return -1;
    if (aDateM.isSame(bDateM)) return 0;
    const sortResult = aDateM.isAfter(bDateM) ? 1 : -1;
    return isAscSort ? sortResult : -sortResult;
  };
};

export const getSortedDates = (dates: string[], sortType = SortType.Asc) => {
  if (!Array.isArray(dates) || dates.length === 0) {
    return [];
  }

  return [...dates].sort(getDateComparator(sortType));
};

/**
 * 把RangeTime组件的value转换为接口入参用的日期信息
 * @param rangeTime RangeTime组件的value
 * @return 接口入参用的日期信息
 */
export const getDateRangeFetchParamsByRangeTimeFilterValue = (rangeTime?: IRangeTimeValue): IDateRangeFetchParams => {
  const result = pageParamsFormatMap[filterKeyMap.RANGE_TIME]?.(rangeTime);
  return {
    granularity: result?.granularity || 'daily',
    start_date: result?.start_date || '',
    end_date: result?.end_date || '',
  };
};

/**
 * 把接口入参用的日期信息转换为RangeTime组件的value
 * @param dateRangeFetchParams 接口入参的日期信息
 * @return RangeTime组件的value
 */
export const getRangeTimeFilterValueByDateRangeFetchParams = (
  dateRangeFetchParams?: IDateRangeFetchParams,
): IRangeTimeValue => {
  const granularity = dateRangeFetchParams?.granularity || 'daily';
  const startDate = dateRangeFetchParams?.start_date || '';
  const endDate = dateRangeFetchParams?.end_date || '';
  const startDateM = getNullableMoment(startDate);
  const endDateM = getNullableMoment(endDate);
  return {
    type: toDateTypeFilter[granularity],
    value: startDateM === null || endDateM === null ? [undefined, undefined] : [startDateM, endDateM],
  };
};

/**
 * 生成标题文案中的日期范围文案
 * 1、不跨日期粒度的情况下，日期文案格式为：in 日期粒度对应的日期字符串，比如 in 2025
 * 2、跨日期粒度的情况下，日期文案格式为：Sum of (开始日期粒度对应的日期字符串-结束日期粒度对应的日期字符串)，比如 Sum of (2024-2025)
 * @param granularity 日期粒度
 * @param startDate 开始日期
 * @param endDate 结束日期
 */
export const getDateRangeInTitle = (granularity?: IGranularity, startDate?: string, endDate?: string) => {
  const startDateM = getNullableMoment(startDate);
  const endDateM = getNullableMoment(endDate);
  if (startDateM === null && endDateM === null) return '';

  const granularityTmp = granularity || 'daily';
  const granularityData = GRANULARITY_MOMENT_HELPER_MAPPING[granularityTmp];
  startDateM?.startOf(granularityData?.dateType as any);
  endDateM?.endOf(granularityData?.dateType as any);

  const startDateFormatted = startDateM!.format(granularityData?.format);
  const endDateFormatted = endDateM!.format(granularityData?.format);
  if (startDateM === null) return `in ${endDateFormatted}`;
  if (endDateM === null) return `in ${startDateFormatted}`;

  // @ts-ignore
  const startDateGranularityValue = startDateM[granularityData?.dateType]();
  // @ts-ignore
  const endDateGranularityValue = endDateM[granularityData?.dateType]();
  // 日期粒度维度上，日期是否相同，比如说 同年、同月、同日
  const isSameDateGranularityValue = startDateGranularityValue === endDateGranularityValue;

  return isSameDateGranularityValue ? `in ${startDateFormatted}` : `Sum of ${startDateFormatted}-${endDateFormatted}`;
};

const momentDateTypeMapping: any = {
  daily: 'day',
  weekly: 'week',
  monthly: 'month',
  quarterly: 'quarter',
  yearly: 'year',
};
const momentDiffMapping: any = {
  daily: 'days',
  weekly: 'weeks',
  monthly: 'months',
  quarterly: 'quarters',
  yearly: 'years',
};
export const getDatesByDateRange = (dateRange?: IDateRangeFetchParams | null): string[] => {
  if (isEmpty(dateRange)) return [];
  const granularity = dateRange!.granularity || 'daily';
  const momentDateType = momentDateTypeMapping[granularity] || 'day';
  const momentDiff = momentDiffMapping[granularity] || 'days';
  const startDate = dateRange!.start_date || '';
  const endDate = dateRange!.end_date || '';
  const startDateM = getNullableMoment(startDate);
  const endDateM = getNullableMoment(endDate);

  startDateM?.startOf(momentDateType);
  endDateM?.endOf(momentDateType);

  if (!startDateM && !endDateM) return [];
  if (!startDateM) return [endDateM!.format('YYYY-MM-DD')];
  if (!endDateM) return [startDateM!.format('YYYY-MM-DD')];

  const offset = endDateM!.diff(startDateM!, momentDiff);
  const dates = Array.from({ length: offset }).map((_, index) => {
    return startDateM!
      .clone()
      .add(index + 1, momentDiff)
      .format('YYYY-MM-DD');
  });

  return [startDateM!.format('YYYY-MM-DD'), ...dates];
};

/**
 * 构建DateRange组件日期禁用的配置
 * 键：日期粒度
 * 值：判定日期是否禁用的回调函数
 * @param metricsGranularityDateRangeMap 各个日期粒度下对应有数据的日期范围的映射
 * @return DateRange组件日期禁用的配置
 */
export const buildDateDisabledCallbackMap = (
  metricsGranularityDateRangeMap?: Partial<Record<IGranularity, IMetricDateRange>>,
): IDateRangeDisabledCallbackDateTypeMap => {
  if (isEmpty(metricsGranularityDateRangeMap)) {
    return {};
  }

  return Object.entries(metricsGranularityDateRangeMap!).reduce((acc, cur) => {
    const granularity = cur?.[0] || 'monthly';
    const granularityDateRange = cur?.[1];
    const granularityMinDate = granularityDateRange?.min_date || '';
    const granularityMaxDate = granularityDateRange?.max_date || '';

    return {
      ...acc,
      [granularity]: (date: moment.Moment) => {
        if (!!granularityMinDate && date.isBefore(granularityMinDate)) {
          return false;
        }

        if (!!granularityMaxDate && date.isAfter(granularityMaxDate)) {
          return false;
        }

        return true;
      },
    };
  }, {});
};
